/**
 * Vanilla JavaScript navbar functionality
 * Handles mobile menu toggle without Bootstrap
 */

document.addEventListener("DOMContentLoaded", function () {
	const navbarCollapse = document.getElementById("navbarNav");
	const navbarToggler = document.querySelector(".navbar-toggler");
	const navbar = document.querySelector(".navbar");

	// ── Dynamic --nav-height ─────────────────────────────────────────
	// Measure actual navbar height and set as CSS variable in px so the
	// pontoon anchor-nav JS (which uses parseInt) reads a correct value.
	// Automatically adapts to different heights at each breakpoint.
	//
	// KWB-83: measure the BAR, not the whole nav. The V3 megamenu panel is
	// in flow beneath the bar, so .navbar.offsetHeight balloons while a panel
	// is open — and every sticky offset on the site reads this value.
	function updateNavHeight() {
		if (!navbar) return;

		const bar = navbar.querySelector(".navbar__bar");
		const inset = parseFloat(
			getComputedStyle(navbar).paddingTop || "0"
		);
		const h = bar ? bar.offsetHeight + inset * 2 : navbar.offsetHeight;

		document.documentElement.style.setProperty("--nav-height", h + "px");

		// KWB-83: the mobile sheet hangs off the bar's real bottom edge. Reading
		// it here covers the WP admin bar (46px, then 32px ≥783px) without the
		// sheet hardcoding those numbers — and it was hardcoding them until a
		// cleanup pass deleted the .admin-bar rule, which put the sheet on top
		// of the capsule.
		const bottom = navbar.getBoundingClientRect().bottom;
		document.documentElement.style.setProperty("--nav-bottom", bottom + "px");
	}
	updateNavHeight();

	let resizeTimer;
	window.addEventListener("resize", function () {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(updateNavHeight, 100);
	});

	// ── Anchor nav scroll override ───────────────────────────────────
	// The plugin's getStickyOffset uses parseInt on --nav-height + anchor
	// nav offsetHeight, but doesn't add breathing room for the section
	// heading. We intercept clicks on capture phase and scroll ourselves,
	// measuring actual sticky elements at click time.
	document.addEventListener(
		"click",
		function (e) {
			const link = e.target.closest(".pontoon-anchor-nav__item");
			if (!link) return;

			e.preventDefault();
			e.stopPropagation();

			const targetId = link.getAttribute("data-target");
			const target = document.getElementById(targetId);
			if (!target) return;

			// Measure whatever is actually fixed/sticky at the top.
			// KWB-83: there is no #fixed-navbar clone any more — the single
			// nav is always fixed, and --nav-height already excludes any open
			// megamenu panel, so read that rather than offsetHeight.
			const anchorNav = document.querySelector(".pontoon-anchor-nav");
			const navH =
				parseInt(
					getComputedStyle(document.documentElement).getPropertyValue(
						"--nav-height"
					),
					10
				) || (navbar ? navbar.offsetHeight : 0);
			const anchorH = anchorNav ? anchorNav.offsetHeight : 0;
			const offset = navH + anchorH + 16; // 16px breathing room

			const top =
				target.getBoundingClientRect().top + window.pageYOffset - offset;
			window.scrollTo({ top: top, behavior: "smooth" });

			// Update active state
			document
				.querySelectorAll(".pontoon-anchor-nav__item")
				.forEach((item) => item.classList.remove("is-active"));
			link.classList.add("is-active");
		},
		true
	); // capture phase — runs before the plugin's handler

	// SEARCH BAR TOGGLE - Function to initialize search functionality
	function initSearchToggle(container = document) {
		const searchToggle = container.querySelector(".search-toggle");
		const searchFormWrapper = container.querySelector(
			".search-form-wrapper"
		);

		if (searchToggle && searchFormWrapper) {
			// Remove existing listeners by cloning
			const newSearchToggle = searchToggle.cloneNode(true);
			searchToggle.parentNode.replaceChild(newSearchToggle, searchToggle);

			newSearchToggle.addEventListener("click", function (e) {
				e.preventDefault();
				e.stopPropagation();
				searchFormWrapper.classList.toggle("active");
			});

			// Close search when clicking outside
			const closeSearchHandler = function (e) {
				const searchBar = container.querySelector(".search-bar");
				if (searchBar && !searchBar.contains(e.target)) {
					searchFormWrapper.classList.remove("active");
				}
			};

			document.removeEventListener("click", closeSearchHandler);
			document.addEventListener("click", closeSearchHandler);
		}
	}

	// Initialize search for main navbar
	initSearchToggle();

	// ── LANG SWITCHER (WPML) ─────────────────────────────────────────
	// Click-to-open dropdown (touch friendly), closes on outside click.
	function initLangSwitcher(root = document) {
		const switcher = root.querySelector(".lang-switcher");
		if (!switcher) return;

		const toggle = switcher.querySelector(".lang-switcher__toggle");
		if (!toggle) return;

		function close() {
			switcher.classList.remove("is-open");
			toggle.setAttribute("aria-expanded", "false");
		}

		toggle.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			const open = switcher.classList.toggle("is-open");
			toggle.setAttribute("aria-expanded", open ? "true" : "false");
		});

		document.addEventListener("click", function (e) {
			if (
				switcher.classList.contains("is-open") &&
				!switcher.contains(e.target)
			) {
				close();
			}
		});
	}

	initLangSwitcher();

	// Megamenu panels are desktop-only; below $bm__xl the burger sheet owns
	// the sub-navigation. (Lived in the scroll block until KWB-83 removed the
	// navbar-cloning code around it.)
	function isDesktop() {
		// Must match $nav-breakpoint in presets/constants.scss — if these two
		// disagree, one viewport range gets the burger AND the hover megamenu.
		return window.innerWidth >= 1280;
	}

	// ── MEGAMENU (desktop) ───────────────────────────────────────────
	// Hovering a .nav-item.dropdown shows the matching .megamenu-panel__item
	// (paired through data-megamenu / data-megamenu-panel). A close delay
	// lets the pointer travel from the link to the panel without closing it.
	function initMegamenu(root = document) {
		const panel = root.querySelector(".megamenu-panel");
		const navItems = root.querySelectorAll(".navbar-nav .nav-item");
		if (!panel || !navItems.length) return;

		const panelItems = panel.querySelectorAll(".megamenu-panel__item");
		let hideTimer = null;

		// KWB-83: the panel deliberately stays where the template put it —
		// inside .bm__container, next to the bar — so it inherits the same
		// inset and rounded-card geometry. V2 moved it out to #main-nav for
		// full-bleed positioning, which would now break the V3 layout.

		panel.classList.remove("is-open");
		panelItems.forEach((p) => p.classList.remove("is-visible"));

		function showPanel(index) {
			clearTimeout(hideTimer);
			let hasContent = false;

			panelItems.forEach((p) => {
				const match = p.getAttribute("data-megamenu-panel") === index;
				p.classList.toggle("is-visible", match);
				if (match) hasContent = true;
			});

			panel.classList.toggle("is-open", hasContent);
			syncAria(hasContent ? index : null);
		}

		// The markup ships aria-haspopup/aria-expanded but nothing was driving
		// them, so screen readers were told every panel was permanently closed.
		function syncAria(openIndex) {
			navItems.forEach((item) => {
				const link = item.querySelector(".nav-link");
				if (!link || !item.classList.contains("dropdown")) return;

				link.setAttribute(
					"aria-expanded",
					item.getAttribute("data-megamenu") === openIndex ? "true" : "false"
				);
			});
		}

		function hidePanel() {
			panel.classList.remove("is-open");
			panelItems.forEach((p) => p.classList.remove("is-visible"));
			syncAria(null);
		}

		function scheduleHide() {
			clearTimeout(hideTimer);
			hideTimer = setTimeout(hidePanel, 150);
		}

		navItems.forEach((item) => {
			item.addEventListener("mouseenter", function () {
				if (!isDesktop()) return;

				if (item.classList.contains("dropdown")) {
					showPanel(item.getAttribute("data-megamenu"));
				} else {
					// Hovering an item without a submenu closes the panel
					scheduleHide();
				}
			});

			item.addEventListener("mouseleave", function () {
				if (!isDesktop()) return;
				scheduleHide();
			});
		});

		// Pointer entering the panel cancels the scheduled close
		panel.addEventListener("mouseenter", () => clearTimeout(hideTimer));
		panel.addEventListener("mouseleave", scheduleHide);

		// ── Touch ───────────────────────────────────────────────────────
		// A tablet wide enough for the desktop nav (a Pixel Tablet is exactly
		// 1280 CSS px landscape) gets the megamenu but has no hover, so the
		// panels were unreachable: tapping a parent just followed its href.
		//
		// First tap on a parent opens its panel, second tap follows the link.
		// Asked per event rather than cached, so docking a mouse to a tablet
		// restores hover behaviour without a reload.
		function canHover() {
			return window.matchMedia("(hover: hover)").matches;
		}

		navItems.forEach((item) => {
			const link = item.querySelector(".nav-link");
			if (!link || !item.classList.contains("dropdown")) return;

			// Tapping focuses the link, and the focusin handler above opens the
			// panel — before click fires. So the open state has to be read
			// earlier than that; pointerdown precedes focus.
			let openBeforeTap = false;

			link.addEventListener("pointerdown", function () {
				openBeforeTap = link.getAttribute("aria-expanded") === "true";
			});

			link.addEventListener("click", function (e) {
				// The mobile list matches .navbar-nav .nav-item too, and has
				// its own .mobile-submenu-toggle buttons.
				if (!isDesktop()) return;
				if (canHover()) return;

				// Already open means this is the second tap: let it navigate.
				if (openBeforeTap) return;

				e.preventDefault();
				showPanel(item.getAttribute("data-megamenu"));
			});
		});


		// ── Keyboard ────────────────────────────────────────────────────
		// Without this the panels are mouse-only: they stay display:none, so
		// their links are out of the tab order and a keyboard user can never
		// reach any sub-navigation item.
		const navRoot = panel.closest(".navbar");

		navItems.forEach((item) => {
			item.addEventListener("focusin", function () {
				if (!isDesktop()) return;

				if (item.classList.contains("dropdown")) {
					showPanel(item.getAttribute("data-megamenu"));
				} else {
					// A top-level link with no panel closes whatever is open.
					scheduleHide();
				}
			});
		});

		// Tabbing into the panel keeps it open.
		panel.addEventListener("focusin", () => clearTimeout(hideTimer));

		// Focus leaving the nav entirely closes it. relatedTarget is null when
		// focus goes to the browser chrome, which should also close.
		if (navRoot) {
			navRoot.addEventListener("focusout", function (e) {
				if (!isDesktop()) return;
				if (e.relatedTarget && navRoot.contains(e.relatedTarget)) return;

				hidePanel();
			});

			// Tapping away closes an open panel. focusout covers taps that move
			// focus; this covers a tap on inert page content, which does not.
			document.addEventListener("click", function (e) {
				if (canHover() || !panel.classList.contains("is-open")) return;
				if (navRoot.contains(e.target)) return;

				hidePanel();
			});
		}

		// ── Moving focus INTO the panel ──────────────────────────────────
		// The panel is a sibling of the whole <ul>, so natural tab order walks
		// every parent link before reaching it — and the panel has closed by
		// then. Tab/ArrowDown from an open parent therefore jumps into the
		// panel explicitly, and Tab off the last item continues to the next
		// parent as if the panel were nested where it visually appears.
		function focusablesIn(el) {
			return Array.prototype.slice
				.call(el.querySelectorAll("a[href], button:not([disabled])"))
				.filter((n) => n.offsetParent !== null);
		}

		function panelItemFor(index) {
			return panel.querySelector('[data-megamenu-panel="' + index + '"]');
		}

		navItems.forEach((item) => {
			const link = item.querySelector(".nav-link");
			if (!link) return;

			link.addEventListener("keydown", function (e) {
				if (!isDesktop() || !item.classList.contains("dropdown")) return;
				if (e.key !== "ArrowDown" && !(e.key === "Tab" && !e.shiftKey)) return;

				const index = item.getAttribute("data-megamenu");
				const target = panelItemFor(index);
				if (!target) return;

				// ArrowDown opens first if needed; Tab only diverts when open.
				if (e.key === "Tab" && !panel.classList.contains("is-open")) return;

				showPanel(index);

				const items = focusablesIn(target);
				if (!items.length) return;

				e.preventDefault();
				items[0].focus();
			});
		});

		panel.addEventListener("keydown", function (e) {
			if (!isDesktop() || e.key !== "Tab") return;

			const visible = panel.querySelector(".megamenu-panel__item.is-visible");
			if (!visible) return;

			const items = focusablesIn(visible);
			if (!items.length) return;

			const index = visible.getAttribute("data-megamenu-panel");
			const all = Array.prototype.slice.call(navItems);
			const triggerItem = all.filter(
				(it) => it.getAttribute("data-megamenu") === index
			)[0];
			const trigger = triggerItem && triggerItem.querySelector(".nav-link");

			// Shift+Tab off the first item goes back to the parent link.
			if (e.shiftKey && document.activeElement === items[0]) {
				e.preventDefault();
				if (trigger) trigger.focus();
				return;
			}

			// Tab off the last item closes and continues to the next parent.
			if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
				const from = all.indexOf(triggerItem);
				let next = null;

				for (let i = from + 1; i < all.length && !next; i++) {
					next = all[i].querySelector(".nav-link");
				}

				hidePanel();

				if (next) {
					e.preventDefault();
					next.focus();
				}
			}
		});

		// Escape closes and returns focus to the trigger, so the user is not
		// dropped back at the top of the document.
		document.addEventListener("keydown", function (e) {
			if (e.key !== "Escape" || !panel.classList.contains("is-open")) return;

			const openItem = Array.from(navItems).find(
				(item) =>
					item.classList.contains("dropdown") &&
					item.querySelector(".nav-link[aria-expanded='true']")
			);

			hidePanel();

			if (openItem) {
				const link = openItem.querySelector(".nav-link");
				if (link) link.focus();
			}
		});
	}

	initMegamenu();

	if (navbarCollapse && navbarToggler && navbar) {
		// Toggle menu function
		function toggleMenu() {
			const isOpen = navbarCollapse.classList.contains("show");

			if (isOpen) {
				closeMenu();
			} else {
				openMenu();
			}
		}

		// KWB-83: the scrim lives on <body> rather than as a pseudo-element on
		// the navbar, so it cannot be affected by the nav's stacking context.
		let scrim = null;

		function showScrim() {
			if (scrim) return;

			scrim = document.createElement("div");
			scrim.className = "menu-scrim";
			scrim.addEventListener("click", closeMenu);
			document.body.appendChild(scrim);

			// Next frame, so the opacity transition has a start value to run from.
			requestAnimationFrame(() => scrim && scrim.classList.add("is-visible"));
		}

		function hideScrim() {
			if (!scrim) return;

			const el = scrim;
			scrim = null;
			el.classList.remove("is-visible");
			setTimeout(() => el.remove(), 250);
		}

		// Open menu function
		function openMenu() {
			navbarCollapse.classList.add("show");
			navbarToggler.setAttribute("aria-expanded", "true");
			navbar.classList.add("active");
			document.body.style.overflow = "hidden";
			showScrim();
		}

		// Close menu function
		function closeMenu() {
			navbarCollapse.classList.remove("show");
			navbarToggler.setAttribute("aria-expanded", "false");
			navbar.classList.remove("active");
			document.body.style.overflow = "";
			hideScrim();
		}


		// Handle burger button click and touch events
		let touchStarted = false;
		let lastAction = 0;

		function handleMenuToggle(e) {
			const now = Date.now();
			if (now - lastAction < 300) return; // Prevent double triggers

			e.preventDefault();
			e.stopPropagation();
			toggleMenu();
			lastAction = now;
		}

		navbarToggler.addEventListener(
			"touchstart",
			function (e) {
				touchStarted = true;
			},
			{ passive: false }
		);

		navbarToggler.addEventListener(
			"touchend",
			function (e) {
				if (touchStarted) {
					handleMenuToggle(e);
					touchStarted = false;
				}
			},
			{ passive: false }
		);

		navbarToggler.addEventListener("click", function (e) {
			if (!touchStarted) {
				handleMenuToggle(e);
			}
		});

		// MOBILE SUBMENU TOGGLE (burger panel accordion)
		// The .mobile-submenu-toggle chevrons are rendered in PHP
		// (menumobile.php); only one submenu open at a time.
		function closeSubmenu(submenu) {
			submenu.classList.remove("show");
			const li = submenu.closest(".nav-item");
			if (li) {
				li.classList.remove("active");
				const btn = li.querySelector(".mobile-submenu-toggle");
				if (btn) {
					btn.classList.remove("active");
					btn.setAttribute("aria-expanded", "false");
				}
			}
		}

		function initMobileSubmenus() {
			const toggles = navbarCollapse.querySelectorAll(
				".mobile-submenu-toggle"
			);

			toggles.forEach((btn) => {
				btn.addEventListener("click", function (e) {
					e.preventDefault();
					e.stopPropagation();

					const parentItem = btn.closest(".nav-item");
					const submenu = parentItem
						? parentItem.querySelector(".menu-mobile__submenu")
						: null;
					if (!submenu) return;

					const isOpen = submenu.classList.contains("show");

					// Close every other submenu
					navbarCollapse
						.querySelectorAll(".menu-mobile__submenu.show")
						.forEach((menu) => {
							if (menu !== submenu) closeSubmenu(menu);
						});

					if (isOpen) {
						closeSubmenu(submenu);
					} else {
						submenu.classList.add("show");
						parentItem.classList.add("active");
						btn.classList.add("active");
						btn.setAttribute("aria-expanded", "true");
					}
				});
			});
		}

		// Init mobile submenus
		initMobileSubmenus();

		// Close menu when clicking on nav links
		const navLinks = navbarCollapse.querySelectorAll(
			".nav-link:not(.dropdown-toggle)"
		);
		navLinks.forEach((link) => {
			link.addEventListener("click", function () {
				// Close the menu after a small delay to allow navigation
				setTimeout(() => {
					closeMenu();
				}, 150);
			});
		});

		// Close menu when clicking outside
		document.addEventListener("click", function (e) {
			if (
				!navbar.contains(e.target) &&
				navbarCollapse.classList.contains("show")
			) {
				closeMenu();
			}
		});
	}

	// ── Scroll behaviour ─────────────────────────────────────────────
	// KWB-83: the V3 bar stays put. V2 hid it on scroll-down and brought it
	// back on scroll-up (and on desktop cloned the whole navbar into a
	// #fixed-navbar copy to do it) — the floating capsule is designed to
	// remain visible over the page, so none of that applies any more.
});
